<template>
    <div
        :style="{
            background: backgroundColor,
            'border-top': hasBorderTop ? border : 'none',
        }"
        class="footer-container"
    >
        <BaseImage
            v-if="backgroundImageSource"
            :imgSource="backgroundImageSource"
            :width="imageDimensions.width"
            :height="imageDimensions.height"
            class="footer-background-img"
            alt="Footer-Background-Image"
            quality="100"
        />
        <div
            :style="{ '--color': color, '--hoverColor': hoverColor }"
            class="wrapper"
        >
            <div class="footer-menu">
                <div class="default-links-wrapper">
                    <router-link
                        v-if="showLogo && !!getLogoSource"
                        :to="{ path: '/' }"
                        class="footer-logo"
                    >
                        <BaseImage
                            v-if="getLogoSource !== 'compado'"
                            :imgSource="getLogoSource"
                            :width="logoWidth"
                            :height="logoHeight"
                            load-lazy
                            quality="90"
                            alt="logo"
                        />
                        <Icon
                            v-else
                            :fill="color"
                            is-local
                            icon-source="compado-horizontal"
                            width="140px"
                            height="40px"
                        />
                    </router-link>

                    <div class="footer-text">{{ $t(getFooterText) }}.</div>

                    <div v-if="showDefaultLinks" class="default-links">
                        <router-link :to="{ name: 'aboutus' }">{{
                            $t('About Us')
                        }}</router-link>
                        <router-link :to="{ name: 'faq' }">{{
                            $t('FAQ')
                        }}</router-link>
                        <router-link :to="{ name: 'imprint' }">{{
                            $globalConstants.host['imprint_text']
                                ? $t($globalConstants.host['imprint_text'])
                                : $t('Imprint')
                        }}</router-link>
                        <router-link :to="{ name: 'privacy' }">{{
                            $t('Privacy')
                        }}</router-link>
                        <template
                            v-if="
                                showReportContent && !isAdsenseExternalProject
                            "
                        >
                            <router-link :to="{ name: 'reportContent' }">{{
                                $t('Report Illegal content')
                            }}</router-link>
                        </template>
                        <template
                            v-if="$globalConstants.host['18_compliance_footer']"
                        >
                            <router-link :to="{ name: 'compliance' }">
                                {{ $t('Custodian of Records') }}
                            </router-link>
                        </template>
                    </div>
                </div>

                <!-- eslint-disable vue/no-use-v-if-with-v-for -->
                <div
                    v-if="!isMinified && isDesktop()"
                    v-for="item in dropdownItems"
                    :key="item.id"
                    class="custom-links-wrapper"
                >
                    <div class="custom-links-title">{{ $t(item.name) }}</div>
                    <div
                        v-for="item in item.dropdown_items.slice(0, 6)"
                        :key="item.id"
                        class="custom-links"
                    >
                        <router-link
                            v-if="!!item.route"
                            :to="{ path: fullPath(item.route) }"
                            >{{ item.name }}</router-link
                        >
                        <p v-else-if="!!item.name">{{ item.name }}</p>
                        <Icon
                            v-if="showArrowIcon"
                            :fill="color"
                            is-local
                            icon-source="arrow-right"
                            class="arrow-right"
                            width="10"
                            height="10"
                        />
                    </div>
                </div>
            </div>

            <div
                v-if="hasDivider"
                :style="{ 'border-bottom': border }"
                class="divider"
            ></div>

            <div v-if="showCopyright" class="copyright">
                Copyright © 2009-{{ fullYear }}
                {{ $globalConstants.host.name }}
            </div>
        </div>
    </div>
</template>

<script>
import { colord } from 'colord';
import { store } from '@/store';
import { helper, mediaQueriesMixin } from '@/mixins';

export default {
    name: 'DefaultFooter',
    components: {
        BaseImage: () => import('@atoms/Image/BaseImage'),
    },
    mixins: [mediaQueriesMixin, helper],
    props: {
        backgroundColor: {
            type: String,
            default: '#fff',
        },
        backgroundImageSource: {
            type: String,
        },
        hasDivider: {
            type: Boolean,
            default: true,
        },
        hasBorderTop: {
            type: Boolean,
            default: false,
        },
        showLogo: {
            type: Boolean,
            default: true,
        },
        logoSource: {
            type: String,
        },
        logoWidth: {
            type: String,
            default: '160',
        },
        logoHeight: {
            type: String,
            default: '50',
        },
        showArrowIcon: {
            type: Boolean,
            default: false,
        },
        isMinified: {
            type: Boolean,
            default: false,
        },
        showCopyright: {
            type: Boolean,
            default: true,
        },
        showDefaultLinks: {
            type: Boolean,
            default: true,
        },
        dropdownItems: {
            type: Array,
            default: () => store.state.dropdownItems.slice(0, 3),
        },
        footerText: {
            type: String,
            default:
                'Top5 analyses and reviews the most popular online services to help you make the most informed decisions',
        },
    },
    data() {
        const color = this.isBackgroundLight() ? '#333' : '#fff';
        return {
            border: `1px solid ${
                this.isBackgroundLight()
                    ? 'rgba(58,58,58,.25)'
                    : 'rgba(255, 255, 255, 0.3)'
            }`,
            color: color,
            hoverColor: colord(color).alpha(0.3).toRgbString(),
            fullYear: new Date().getFullYear(),
        };
    },
    computed: {
        isAdsenseExternalProject() {
            return this.$globalConstants.host.project_id === 108;
        },
        imageDimensions() {
            return {
                width: this.isDesktop() ? '1200' : '720',
                height: this.isDesktop() ? '400' : '140',
            };
        },
        getFooterText() {
            if (this.$globalConstants.host['footer_text']) {
                return this.$globalConstants.host['footer_text'];
            }

            return this.footerText
                ? this.footerText
                : 'Top5 analyses and reviews the most popular online services to help you make the most informed decisions';
        },
        getLogoSource() {
            if (this.logoSource) {
                return this.logoSource;
            }
            if (this.$globalConstants.host.host_footer) {
                return this.$globalConstants.host.host_footer;
            }
            return this.$globalConstants.host.header_banner;
        },
    },
    watch: {
        backgroundColor: function () {
            const color = this.isBackgroundLight() ? '#333' : '#fff';
            this.border = `1px solid ${
                this.isBackgroundLight()
                    ? 'rgba(58,58,58,.25)'
                    : 'rgba(255, 255, 255, 0.3)'
            }`;
            this.color = color;
            this.hoverColor = colord(color).alpha(0.3).toRgbString();
        },
    },
    methods: {
        isBackgroundLight() {
            return colord(this.backgroundColor).isLight();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/mixins';
@import '@/sass/flex-gap';

a {
    text-decoration: none;
    color: var(--color);
    &:hover {
        color: var(--hoverColor);
    }
}

.footer-container {
    display: flex;
    justify-content: center;
    width: 100%;
    position: relative;
    z-index: 1;
}

.footer-background-img {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
}

.wrapper {
    display: flex;
    flex-direction: column;
    max-width: 1180px;
    padding: 20px 20px 30px;
    width: 100%;
    @include tablet {
        padding: 30px 40px 30px;
    }
    @include desktop {
        padding: 30px 40px 30px;
    }
    z-index: 1;
}

.footer-menu {
    @include row-gap(40px);
    margin-bottom: 20px;
    @include tablet {
        margin-bottom: 30px;
    }
    @include desktop {
        margin-bottom: 30px;
    }
}

.default-links-wrapper {
    display: flex;
    flex-direction: column;
    flex: 4 5 auto;
    @include desktop {
        margin-right: 40px;
    }

    .footer-logo {
        margin-bottom: 20px;
        @include mobile {
            img {
                max-width: 80px;
                max-height: 20px;
            }
            margin-bottom: 10px;
        }
    }

    .footer-text {
        font-size: 12px;
        line-height: 18px;
        letter-spacing: 0.02em;
        color: var(--color);
    }

    .default-links {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        margin-top: 20px;
        a {
            font-weight: 500;
            font-size: 12px;
            line-height: 20px;
            letter-spacing: 0.02em;
            margin-bottom: 5px;
            @include tablet {
                font-size: 16px;
            }
            @include desktop {
                font-size: 16px;
            }
        }
    }
}

.custom-links-wrapper {
    flex-direction: column;
    flex: 1 1 auto;
    color: var(--color);

    .custom-links-title {
        font-weight: bold;
        font-size: 16px;
        line-height: 20px;
        letter-spacing: 0.02em;
        margin-bottom: 10px;
    }

    .custom-links {
        font-size: 13px;
        line-height: 20px;
        letter-spacing: 0.02em;
        display: flex;
        justify-content: space-between;
        align-items: center;

        a {
            margin-right: 8px;
        }

        &:not(:last-of-type) {
            margin-bottom: 5px;
        }

        &:hover {
            path {
                fill: var(--hoverColor);
            }
        }
    }
}

.divider {
    width: 100%;
}

.copyright {
    font-size: 10px;
    line-height: 26px;
    letter-spacing: 0.02em;
    color: var(--color);
    margin-top: 10px;
    @include tablet {
        margin-top: 20px;
    }
    @include desktop {
        margin-top: 20px;
    }
}
</style>
